import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface ScheduledStream {
  id: number;
  title: string;
  streamer: string;
  avatar: string;
  scheduledFor: string;
  date: string;
  time: string;
  thumbnail: string;
  category: string;
  subscribers: number;
  isNotified: boolean;
}

export default function ScheduledStreams() {
  const [streams, setStreams] = useState<ScheduledStream[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScheduledStreams = async () => {
      const { data, error } = await supabase
        .from('streams')
        .select(`
          *,
          profiles!streams_streamer_id_fkey (
            username,
            avatar_url
          )
        `)
        .eq('is_live', false)
        .not('scheduled_for', 'is', null)
        .gte('scheduled_for', new Date().toISOString())
        .order('scheduled_for', { ascending: true })
        .limit(6);

      if (error) {
        console.error('Error fetching scheduled streams:', error);
      } else {
        setStreams(data || []);
      }
      setLoading(false);
    };

    fetchScheduledStreams();
  }, []);

  const toggleNotification = (id: number) => {
    setStreams(streams.map(stream => 
      stream.id === id ? { ...stream, isNotified: !stream.isNotified } : stream
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Scheduled Streams</h2>
        <p className="text-gray-400">Set reminders for upcoming streams you don't want to miss</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {streams.map((stream) => (
          <div
            key={stream.id}
            className="bg-gray-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-red-600 transition-all group"
          >
            <div className="relative">
              <div className="w-full h-48">
                <img
                  src={stream.thumbnail}
                  alt={stream.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute top-3 left-3 bg-orange-600 text-white px-3 py-1 rounded-md font-bold text-sm flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                {stream.scheduledFor}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-3 left-3 right-3">
                <div className="text-white font-semibold text-sm mb-1">{stream.date}</div>
                <div className="text-white text-lg font-bold">{stream.time}</div>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img
                    src={stream.avatar}
                    alt={stream.streamer}
                    className="w-full h-full rounded-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold mb-1 group-hover:text-red-400 transition-colors line-clamp-2">
                    {stream.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">{stream.streamer}</p>
                  <div className="flex items-center gap-3">
                    <span className="inline-block bg-gray-800 text-gray-300 px-2 py-1 rounded text-xs">
                      {stream.category}
                    </span>
                    <span className="text-gray-500 text-xs flex items-center">
                      <i className="ri-user-line mr-1"></i>
                      {stream.subscribers.toLocaleString()} subs
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => toggleNotification(stream.id)}
                className={`w-full py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center ${
                  stream.isNotified
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {stream.isNotified ? (
                  <>
                    <i className="ri-notification-fill mr-2"></i>
                    Reminder Set
                  </>
                ) : (
                  <>
                    <i className="ri-notification-line mr-2"></i>
                    Set Reminder
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
